import { useState, useEffect } from 'react';
import { Switch, Tooltip, CircularProgress, Snackbar, Alert, IconButton } from '@mui/material';
import Icon from '@mui/material/Icon';
import { useNavigate } from 'react-router-dom';
import { toggleExcludeFromMetrics } from 'services/clients';

const ExcludedFromMetricsCell = ({ value, row }) => {
  const [isExcluded, setIsExcluded] = useState(Boolean(value));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync state with prop value when it changes (e.g., after table refresh)
  useEffect(() => {
    setIsExcluded(Boolean(value));
  }, [value]);

  const handleChange = async (event) => {
    const newValue = event.target.checked;
    const previousValue = isExcluded;

    // Optimistic update
    setIsExcluded(newValue);
    setIsLoading(true);
    setError(null);

    try {
      await toggleExcludeFromMetrics(row.original.id, newValue);
    } catch (err) {
      console.error('Failed to update exclusion status:', err);
      // Revert on error
      setIsExcluded(previousValue);
      setError(`Failed to update: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Tooltip
        title={isExcluded ? 'Excluded from metrics (click to include)' : 'Included in metrics (click to exclude)'}
      >
        <span>
          <Switch
            checked={isExcluded}
            onChange={handleChange}
            color={isExcluded ? 'warning' : 'success'}
            size='small'
            disabled={isLoading}
          />
        </span>
      </Tooltip>
      {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
      <Snackbar open={!!error} autoHideDuration={5000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity='error' sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

const ProviderConfigCell = ({ row }) => {
  const navigate = useNavigate();
  return (
    <Tooltip title='View Provider Config'>
      <IconButton
        size='small'
        color='info'
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/client-management/provider-config/${row.original.id}`);
        }}
      >
        <Icon fontSize='small'>settings</Icon>
      </IconButton>
    </Tooltip>
  );
};

const clientColumnData = [
  {
    Header: 'Id',
    accessor: 'id'
  },
  {
    Header: 'Name',
    accessor: 'name'
  },
  {
    Header: 'Callback URL',
    accessor: 'callbackUrl'
  },
  {
    Header: 'Whitelisted IPs',
    accessor: 'whitelistedIps'
  },
  {
    Header: 'Excluded from Metrics',
    accessor: 'isExcludedFromMetrics',
    Cell: ExcludedFromMetricsCell
  },
  {
    Header: 'Created At',
    accessor: 'createdAt'
  },
  {
    Header: 'Updated At',
    accessor: 'updatedAt'
  },
  {
    Header: 'Config',
    accessor: 'configAction',
    Cell: ProviderConfigCell
  }
];
export default clientColumnData;
